import { z } from "zod";

import type { ChatAttachmentResourceType } from "@/features/chat/chatAttachments";
import { browserApiFetch } from "@/shared/api/browserApiClient";

const uploadDescriptorSchema = z
  .object({
    expires_at: z.string().datetime({ offset: true }),
    method: z.literal("PUT"),
    signed_headers: z.record(z.string(), z.string()),
    url: z.string().url(),
  })
  .strict();

const uploadIdentitySchema = z
  .object({ upload_id: z.string().min(1) })
  .passthrough();

const createUploadResponseSchema = z
  .object({
    expires_at: z.string().datetime({ offset: true }),
    method: z.enum(["single", "multipart"]),
    part_count: z.number().int().nonnegative(),
    part_size_bytes: z.number().int().nonnegative(),
    resource_id: z.string().min(1),
    upload: uploadDescriptorSchema.optional(),
    upload_id: z.string().min(1),
  })
  .strict();

const completeUploadResponseSchema = z
  .object({
    resource: z
      .object({
        available_at: z.string().datetime({ offset: true }),
        checksum: z.string(),
        created_at: z.string().datetime({ offset: true }),
        deleted_at: z.string().datetime({ offset: true }).nullable(),
        display_name: z.string().min(1),
        metadata_summary: z.record(z.string(), z.unknown()),
        mime_type: z.string().min(1),
        name: z.string().min(1),
        resource_id: z.string().min(1),
        resource_type: z.enum(["image", "video", "file"]),
        revision: z.number().int().positive(),
        size_bytes: z.number().int().nonnegative(),
        source_kind: z.string().min(1),
        status: z.literal("available"),
        version: z.string(),
      })
      .strict(),
  })
  .strict();

const MAX_PART_UPLOAD_CONCURRENCY = 3;

export class CloudResourceUploadError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = "CloudResourceUploadError";
    this.code = code;
  }
}

export interface CloudResourceUploadResult {
  displayName: string;
  resourceId: string;
  resourceType: ChatAttachmentResourceType;
}

interface UploadCloudResourceOptions {
  idempotencyKey?: string;
  signal?: AbortSignal;
  sourceKind?: "conversation_attachment" | "user_upload";
}

function uploadPath(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}/cloud-drive/uploads`;
}

function signedUploadHeaders(values: Readonly<Record<string, string>>) {
  const headers = new Headers();
  for (const [key, value] of Object.entries(values)) {
    if (key.toLowerCase() !== "content-length") headers.set(key, value);
  }
  return headers;
}

async function putSignedBody(
  descriptor: z.infer<typeof uploadDescriptorSchema>,
  body: Blob,
  signal: AbortSignal | undefined,
) {
  const response = await fetch(descriptor.url, {
    body,
    headers: signedUploadHeaders(descriptor.signed_headers),
    method: descriptor.method,
    signal,
  });
  if (!response.ok) {
    throw new CloudResourceUploadError("cloud_upload_put_failed");
  }
  return response;
}

async function requestJson<T>(
  path: string,
  init: RequestInit,
  schema: z.ZodType<T>,
) {
  const response = await browserApiFetch(path, init);
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new CloudResourceUploadError(apiErrorCode(payload));
  }
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new CloudResourceUploadError("cloud_upload_invalid_response");
  }
  return parsed.data;
}

function apiErrorCode(payload: unknown) {
  return payload &&
    typeof payload === "object" &&
    "error" in payload &&
    typeof payload.error === "string"
    ? payload.error
    : "cloud_upload_request_failed";
}

async function bestEffortCancel(organizationId: string, uploadId: string) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5_000);
  try {
    await browserApiFetch(
      `${uploadPath(organizationId)}/${encodeURIComponent(uploadId)}/cancel`,
      { method: "POST", signal: controller.signal },
    );
  } catch {
    return;
  } finally {
    clearTimeout(timeout);
  }
}

async function requestCreateUpload(
  organizationId: string,
  init: RequestInit,
) {
  const response = await browserApiFetch(uploadPath(organizationId), init);
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new CloudResourceUploadError(apiErrorCode(payload));
  }
  const parsed = createUploadResponseSchema.safeParse(payload);
  if (parsed.success) return parsed.data;

  const identity = uploadIdentitySchema.safeParse(payload);
  if (identity.success) {
    await bestEffortCancel(organizationId, identity.data.upload_id);
  }
  throw new CloudResourceUploadError("cloud_upload_invalid_response");
}

async function uploadMultipartParts(
  organizationId: string,
  uploadId: string,
  file: File,
  partSizeBytes: number,
  partCount: number,
  signal: AbortSignal | undefined,
) {
  if (partSizeBytes <= 0 || partCount <= 0) {
    throw new CloudResourceUploadError("cloud_upload_invalid_multipart");
  }
  if (Math.ceil(file.size / partSizeBytes) !== partCount) {
    throw new CloudResourceUploadError("cloud_upload_invalid_multipart");
  }

  const completedParts: Array<{ etag: string; part_number: number }> = [];
  const partController = new AbortController();
  const abortParts = () => partController.abort();
  if (signal?.aborted) partController.abort();
  signal?.addEventListener("abort", abortParts, { once: true });
  let nextPartNumber = 1;

  const uploadNextPart = async () => {
    while (nextPartNumber <= partCount) {
      const partNumber = nextPartNumber;
      nextPartNumber += 1;
      const descriptor = await requestJson(
        `${uploadPath(organizationId)}/${encodeURIComponent(uploadId)}/parts/${partNumber}/sign`,
        { method: "POST", signal: partController.signal },
        uploadDescriptorSchema,
      );
      const start = (partNumber - 1) * partSizeBytes;
      const response = await putSignedBody(
        descriptor,
        file.slice(start, Math.min(start + partSizeBytes, file.size)),
        partController.signal,
      );
      const etag = response.headers.get("ETag")?.trim();
      if (!etag) {
        throw new CloudResourceUploadError("cloud_upload_etag_unavailable");
      }
      completedParts.push({ etag, part_number: partNumber });
    }
  };

  try {
    await Promise.all(
      Array.from(
        { length: Math.min(MAX_PART_UPLOAD_CONCURRENCY, partCount) },
        uploadNextPart,
      ),
    );
    return completedParts.sort(
      (left, right) => left.part_number - right.part_number,
    );
  } catch (error) {
    partController.abort();
    throw error;
  } finally {
    signal?.removeEventListener("abort", abortParts);
  }
}

export async function uploadConversationAttachment(
  organizationId: string,
  file: File,
  resourceType: ChatAttachmentResourceType,
  options: UploadCloudResourceOptions = {},
): Promise<CloudResourceUploadResult> {
  const mimeType = file.type.trim() || "application/octet-stream";
  const created = await requestCreateUpload(
    organizationId,
    {
      body: JSON.stringify({
        idempotency_key: options.idempotencyKey ?? crypto.randomUUID(),
        mime_type: mimeType,
        original_name: file.name,
        resource_type: resourceType,
        size_bytes: file.size,
        source_kind: options.sourceKind ?? "conversation_attachment",
      }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
      signal: options.signal,
    },
  );

  try {
    let parts: Array<{ etag: string; part_number: number }> = [];
    if (created.method === "single") {
      if (!created.upload) {
        throw new CloudResourceUploadError("cloud_upload_descriptor_missing");
      }
      await putSignedBody(created.upload, file, options.signal);
    } else {
      parts = await uploadMultipartParts(
        organizationId,
        created.upload_id,
        file,
        created.part_size_bytes,
        created.part_count,
        options.signal,
      );
    }

    const completed = await requestJson(
      `${uploadPath(organizationId)}/${encodeURIComponent(created.upload_id)}/complete`,
      {
        body: JSON.stringify({ parts }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
        signal: options.signal,
      },
      completeUploadResponseSchema,
    );
    if (completed.resource.resource_id !== created.resource_id) {
      throw new CloudResourceUploadError("cloud_upload_identity_mismatch");
    }
    return {
      displayName: completed.resource.display_name,
      resourceId: completed.resource.resource_id,
      resourceType: completed.resource.resource_type,
    };
  } catch (error) {
    await bestEffortCancel(organizationId, created.upload_id);
    throw error;
  }
}

export function cloudUploadErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "上传已取消";
  }
  const code =
    error instanceof CloudResourceUploadError ? error.code : "unknown";
  if (code === "cloud_storage_quota_exceeded") {
    return "云存储已用完，暂时无法上传新文件";
  }
  if (code === "cloud_upload_etag_unavailable") {
    return "分片上传响应缺少 ETag，请稍后重试";
  }
  if (code === "cloud_upload_invalid_response") return "上传服务响应异常";
  if (
    code === "cloud_upload_invalid_multipart" ||
    code === "cloud_upload_identity_mismatch"
  ) {
    return "上传服务响应异常";
  }
  return "上传失败，请重试";
}
